// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import { applyPenders } from 'lib/common';
import * as AuthAPI from 'lib/api/auth';
import { SettingsState } from './settings';

/* ACTION TYPE */
const CHECK_USER = 'user/CHECK_USER';
const SET_USER = 'user/SET_USER';
const UPDATE_USER = 'user/UPDATE_USER';
const PROCESS_USER = 'user/PROCESS_USER';
const LOGOUT = 'user/LOGOUT';

type SetUserPayload = {
  id: string,
  managerName: string,
  displayName: string,
  mobile: string,
  bizNumber: ?string,
  thumbnail?: ?string,
  username: string,
  email: string,
  rule?: ?string,
  grade?: ?string,
  address?: ?string,
};

type UpdateUserPayload = {
  managerName: string,
  displayName: string,
  mobile: string,
  bizNumber: ?string,
};

/* ACTION CREATOR */
const checkUser = createAction(CHECK_USER, AuthAPI.check);
const updateUser = createAction(UPDATE_USER, (payload: UpdateUserPayload) => payload);
const setUser = createAction(SET_USER, (payload: SetUserPayload) => payload);
const processUser = createAction(PROCESS_USER);
const logout = createAction(LOGOUT, AuthAPI.logout);

/* ACTION FLOW TYPE */
type CheckUserAction = ActionType<typeof checkUser>;
type UpdateUserAction = ActionType<typeof updateUser>;
type SetUserAction = ActionType<typeof setUser>;
type ProcessUserAction = ActionType<typeof processUser>;
type LogoutAction = ActionType<typeof logout>;

/* ACTION CREATORS INTERFACE */
export interface UserActionCreators {
  checkUser(): any;

  updateUser(payload: UpdateUserAction): any;

  setUser(payload: SetUserPayload): any;

  processUser(): any;

  logout(): any;
}

/* EXPORT ACTION CREATORS */
export const actionCreators: UserActionCreators = {
  checkUser,
  updateUser,
  setUser,
  processUser,
  logout,
};

/* STATE TYPES */
export type UserData = {
  id: string,
  managerName: string,
  displayName: string,
  mobile: string,
  bizNumber: ?string,
  thumbnail?: ?string,
  username: string,
  email: string,
  rule?: ?string,
  grade?: ?string,
  address?: ?string,
};

export type User = {
  user: ?UserData,
  processed: boolean,
};

/* INITIAL STATE */
const initialState: User = {
  user: null,
  processed: false,
};

/* REDUCER */
const reducer = handleActions(
  {
    [UPDATE_USER]: (state, action: UpdateUserAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.user.managerName = action.payload.managerName;
        draft.user.displayName = action.payload.displayName;
        draft.user.mobile = action.payload.mobile;
        draft.user.bizNumber = action.payload.bizNumber;
      });
    },
    [SET_USER]: (state, action: SetUserAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.user = action.payload;
      });
    },
    [PROCESS_USER]: (state, action: ProcessUserAction) => {
      return produce(state, draft => {
        draft.processed = true;
      });
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: CHECK_USER,
    onSuccess: (state, { payload: { data } }) => {
      const { user } = data;
      return produce(state, draft => {
        draft.user = user;
        draft.processed = true;
      });
    },
    onError: state =>
      produce(state, draft => {
        draft.user = null;
        draft.processed = true;
      }),
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/user.js